import { useCallback, useMemo, useState } from 'react';
import { ModelProvider, ModelRegistryType, ProviderMetadata } from '../types';
import { fetchProviderModelRegistry } from '../services/api';
import i18n from '../i18n';

/**
 * State and mechanics shared by every "configure a model" dialog (Command
 * Center's platform-wide ConfigureModelDialog and the org-facing
 * OrgModelConfigureDialog) — the two-step type/provider/model selection,
 * the dynamic-fields form state, and registry-backed validation. Each
 * dialog still owns its own load-on-open effect and handleSave, since those
 * differ (OOTB-edit disabling, allowlist/utility bookkeeping) enough that
 * merging them would risk behavior neither dialog asked for.
 */
export function useModelConfigureForm(providers: ProviderMetadata[]) {
  const [step, setStep] = useState<1 | 2>(1);
  const [type, setType] = useState<ModelRegistryType>('llm');
  const [providerKey, setProviderKey] = useState<string>('');
  const [model, setModel] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [values, setValues] = useState<Record<string, string>>({});
  const [loadedValues, setLoadedValues] = useState<Record<string, string>>({});
  const [dirtyFields, setDirtyFields] = useState<Set<string>>(new Set());
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const configTypeMeta = useMemo(() => {
    const providerMeta = providers.find((p) => p.key === providerKey);
    return providerMeta ? providerMeta[type] : undefined;
  }, [providers, providerKey, type]);

  // Model is chosen once, in Step 1 — Step 2's DynamicConfigFields only
  // renders credentials/behavioral fields, not a second model picker.
  const step2Fields = useMemo(
    () => configTypeMeta?.fields.filter((f) => f.name !== 'model') ?? [],
    [configTypeMeta],
  );

  const resetSelection = useCallback(() => {
    setModel('');
    setValues({});
    setDirtyFields(new Set());
  }, []);

  const handleFieldChange = useCallback((fieldName: string, value: string) => {
    setValues((prev) => ({ ...prev, [fieldName]: value }));
  }, []);

  const handleFieldDirty = useCallback((fieldName: string) => {
    setDirtyFields((prev) => new Set(prev).add(fieldName));
  }, []);

  const findRegistryEntry = useCallback(async () => {
    const registryEntries = await fetchProviderModelRegistry({ provider: providerKey as ModelProvider, type });
    const registryEntry = registryEntries.find((entry) => entry.model === model);
    if (!registryEntry) {
      throw new Error(i18n.t('errors.models.notInRegistry'));
    }
    return registryEntry;
  }, [providerKey, type, model]);

  return {
    step, setStep,
    type, setType,
    providerKey, setProviderKey,
    model, setModel,
    name, setName,
    description, setDescription,
    values, setValues,
    loadedValues, setLoadedValues,
    dirtyFields, setDirtyFields,
    isSaving, setIsSaving,
    error, setError,
    configTypeMeta,
    step2Fields,
    resetSelection,
    handleFieldChange,
    handleFieldDirty,
    findRegistryEntry,
  };
}
